<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useDonationDetailModal } from '@/composables/useDonationDetailModal'
import { useDetailNavigation } from '@/composables/useDetailNavigation'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'
import DetailFrameView from '@/components/dashboard/modals/DetailFrameView.vue'
import debug from '@/helpers/debug'
import type { DonorDonation } from '@/types/donorFinancialYear'

const { isOpen, closeDonationDetailModal, patchSelectedDonation } =
  useDonationDetailModal()
const {
  stack,
  root,
  top,
  canGoBack,
  pushDonation,
  backDetail,
  closeDetailNavigation,
  removeDonationFromNavigation,
  removeSeriesFromNavigation,
} = useDetailNavigation()

const detailBusy = ref(false)

const isHost = computed(() => isOpen.value && root.value?.kind === 'donation')

const recurringTitleId = ref('recurring-manage-title')

const titleId = computed(() =>
  top.value?.kind === 'recurring'
    ? recurringTitleId.value
    : 'donation-detail-title'
)

const modalClass = computed(() => {
  const framed = 'sa-donation-detail-modal sa-donation-detail-modal--framed'
  if (top.value?.kind === 'recurring') return `${framed} sa-recurring-modal`
  return framed
})

const closeAll = () => {
  debug('Closing the donation detail modal', {
    depth: stack.value.length,
  })
  closeDonationDetailModal()
}

const goBack = async () => {
  if (detailBusy.value) return
  const leaving = top.value
  debug('Going back in the donation detail modal', {
    from: leaving?.kind,
    depth: stack.value.length,
  })
  backDetail()
  detailBusy.value = false
  await nextTick()
  const back = document.getElementById('detail-nav-back')
  if (back) {
    back.focus()
    return
  }
  document.querySelector<HTMLElement>('.sa-modal-close')?.focus()
}

const onHistoryDonation = (donation: DonorDonation) => {
  pushDonation(donation)
  detailBusy.value = false
  void nextTick(() => document.getElementById('detail-nav-back')?.focus())
}

const onDonationDeleted = (donationId: string) => {
  // Remove that gift, not whatever happens to be on top. Manage series can be
  // opened while the delete is still in flight.
  removeDonationFromNavigation(donationId)
  detailBusy.value = false
  if (!stack.value.length || root.value?.kind !== 'donation') {
    closeDetailNavigation()
    closeAll()
  }
}

const onUpdated = (updated: DonorDonation) => {
  patchSelectedDonation(updated)
}

const onSeriesDeleted = (seriesId: string) => {
  removeSeriesFromNavigation(seriesId)
  detailBusy.value = false
  if (!stack.value.length || root.value?.kind !== 'donation') {
    closeDonationDetailModal()
  }
}
</script>

<template>
  <!--
    One backdrop for the whole detail stack. The first frame is the gift
    opened from Donation history. Later frames replace the contents.
  -->
  <DashboardModal
    :is-open="isHost"
    :modal-class="modalClass"
    :aria-labelledby="titleId"
    :close-disabled="detailBusy"
    @close="closeAll"
  >
    <template v-if="canGoBack" #header-start>
      <button
        id="detail-nav-back"
        type="button"
        class="sa-modal-back"
        :disabled="detailBusy"
        @click="goBack"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.RecurringDonationModal.Back') }}
      </button>
    </template>

    <!--
      Only the current detail is shown. Earlier ones stay cached, so Back
      restores expanded sections and scroll without a second backdrop.
    -->
    <div class="sa-detail-stack">
      <KeepAlive>
        <DetailFrameView
          v-if="top"
          :key="top.key"
          :frame="top"
          @busy="detailBusy = $event"
          @deleted="onDonationDeleted"
          @updated="onUpdated"
          @open-history="onHistoryDonation"
          @series-deleted="onSeriesDeleted"
          @labelled-by="recurringTitleId = $event"
        />
      </KeepAlive>
    </div>
  </DashboardModal>
</template>
